// ============================================================
// ROSTER REPLACE - one person stands in for another, on the same
// spot (v8.342.0).
//
// "This guardsman was a Genestealer all along" used to be a recall and
// an import: the token was deleted with the departed and a fresh one
// was rung at the deployment marker for the newcomer, so the GM then
// dragged them back to where the old one stood. A replace is a SWAP:
// the newcomer takes the departed's feet and their slot in the turn
// order, and the departed goes into a waiting room with their wounds
// and conditions intact.
//
// It is a NEW combatant id, not `identity.ts`'s in-place sheet swap.
// That path keeps the id because it is the same person corrected; this
// is a different person, and aliasing them would hand the newcomer the
// departed's undo snapshots and defence documents.
//
// Pure. The hook (`useWaitingRoomActions.replaceWith`) does the writes
// in the order its header comment states; this module decides what
// they contain and whether they may happen at all.
// ============================================================
import type { Combatant } from './types';
import type { Token } from '../battlefield/tokens';
import { tokenForSize } from '../battlefield/tokens';
import { projectDefender } from './projection';
import { teamOf } from './team';

export interface ReplacePlan {
  /** The newcomer's token, standing where the departed stood. */
  token: Token;
}

/**
 * The newcomer's token on the departed's feet.
 *
 * Position, elevation and the terrain exemption are the SPOT's - they
 * belong to where someone stands, not to who stands there. Height, eye
 * height and footprint are the NEWCOMER's, re-derived through
 * `tokenForSize` exactly as an arrival's would be: `reconcileTokens`
 * never resizes, so reusing the departed's fields would leave a
 * Carnifex peering over walls at a guardsman's eye height for the rest
 * of the fight. The heading is a hull's, so it carries only when the
 * newcomer has one to point.
 *
 * `rev: 1` - a new token, not the departed's clock continued.
 */
export function planReplace(args: { outgoing: Combatant; incoming: Combatant; token: Token }): ReplacePlan {
  const { incoming, token } = args;
  const size = projectDefender(incoming)?.size;
  const isVehicle = incoming.sheet.bodyType === 'vehicle';
  return {
    token: tokenForSize(incoming.id, size, {
      x: token.x,
      z: token.z,
      elevation: token.elevation,
      rev: 1,
      // Spread conditionally: Firestore rejects a literal `undefined`,
      // and `setBattlefieldToken` only carries these when present.
      ...(token.ignoresTerrain ? { ignoresTerrain: true } : {}),
      ...(isVehicle && token.heading !== undefined ? { heading: token.heading } : {}),
    }),
  };
}

/**
 * The deploy stamp's semantics, applied to a stand-in: a newcomer with
 * a team keeps it; one without inherits the departed's, so a replaced
 * Kill-team member is still on the Kill-team. A blank tag is not a
 * team (`teamOf`), so a half-filled Team tab inherits nothing.
 *
 * Returns the SAME object when there is nothing to change - the
 * projection cache keys on identity, and a needless copy would cost
 * every panel a re-projection.
 */
export function inheritTeam(incoming: Combatant, outgoing: Combatant): Combatant {
  if (teamOf(incoming)) return incoming;
  const team = teamOf(outgoing);
  if (!team) return incoming;
  return { ...incoming, sheet: { ...incoming.sheet, team: { ...team } } };
}

export interface RefuseReplaceArgs {
  /** Combatant ids with a defence prompt open - `pendingDefences.keys()`. */
  pendingIds: ReadonlySet<string>;
  outgoingId: string;
  incomingId: string;
  roster: readonly Combatant[];
  tokens: Readonly<Record<string, Token>>;
}

/**
 * Why a replace may not happen right now, as a sentence for the
 * dialog - or null when it may. Checked BEFORE anything moves.
 *
 * A pending defence is the one that matters: the prompt is keyed by the
 * departed's id, and once they leave the roster it resolves against
 * nobody, silently ending an attack in flight.
 */
export function refuseReplace(args: RefuseReplaceArgs): string | null {
  const { pendingIds, outgoingId, incomingId, roster, tokens } = args;
  const outgoing = roster.find((c) => c.id === outgoingId);
  if (!outgoing) return 'They are not on the roster.';
  if (!tokens[outgoingId]) return `${outgoing.name} is not on the map - there is no spot to stand on.`;
  if (pendingIds.has(outgoingId)) return `${outgoing.name} is answering an attack - resolve the defence first.`;
  if (incomingId === outgoingId || roster.some((c) => c.id === incomingId)) {
    return 'The newcomer is already on the roster.';
  }
  return null;
}
